import { getAuth } from "firebase/auth";
import { unitsTable } from "../../helpers/tables";
import { queryMany, queryOne, count } from "./all";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getFirestore,
  updateDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getApiUrl } from "../api/config";

const table = unitsTable;

const unitModel = {
  create: async (props) => {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not logged in");

    const token = await user.getIdToken();
    const res = await fetch(`${getApiUrl()}/units`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(props),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 403) {
      const err = new Error(
        data.error || "Portfolio limit reached. Upgrade to add more doors."
      );
      err.code = "PORTFOLIO_LIMIT";
      throw err;
    }
    if (!res.ok) {
      throw new Error(data.error || "Failed to create door");
    }
    const id = data.id;
    if (!id) throw new Error("Unit not created");
    const docRow = await queryOne({ table, id });
    if (docRow) return docRow;
    return { id, ...data };
  },

  getOneById: async (id) => {
    const data = await queryOne({ table, id });
    return data;
  },

  getMany: async (...args) => {
    const data = await queryMany({
      table,
      conditions: [...args],
    });
    return data;
  },

  getByOwner: async (ownerId) => {
    const data = await queryMany({
      table,
      conditions: [["ownerId", "==", ownerId]],
    });
    return data;
  },

  update: async (id, data) => {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not logged in");

    const unitRef = doc(getFirestore(), table, id);
    await updateDoc(unitRef, {
      ...data,
      updatedAt: new Date(),
    });

    return await queryOne({ table, id });
  },

  delete: async (id) => {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not logged in");

    await deleteDoc(doc(getFirestore(), table, id));
  },

  count: async (...args) => {
    const data = await count({
      table,
      conditions: [...args],
    });
    return data;
  },

  listen: async (callback) => {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not logged in");

    const q = query(collection(db, table), where("ownerId", "==", user.uid));

    const sub = onSnapshot(q, (querySnapshot) => {
      const properties = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(properties);
    });

    return sub;
  },

  // Specialized queries for unit management
  getActiveUnits: async (ownerId) => {
    const data = await queryMany({
      table,
      conditions: [
        ["ownerId", "==", ownerId],
        ["status", "==", "active"],
      ],
    });
    return data;
  },

  getUnitsByType: async (ownerId, unitType) => {
    const data = await queryMany({
      table,
      conditions: [
        ["ownerId", "==", ownerId],
        ["unitType", "==", unitType],
      ],
    });
    return data;
  },

  getUnitsByStatus: async (ownerId, status) => {
    const data = await queryMany({
      table,
      conditions: [
        ["ownerId", "==", ownerId],
        ["status", "==", status],
      ],
    });
    return data;
  },

  // Rent management methods
  updateRent: async (unitId, newRent, reason = "rent_adjustment") => {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not logged in");

    const unit = await queryOne({ table, id: unitId });
    if (!unit) throw new Error("Unit not found");

    const rentHistoryEntry = {
      date: new Date(),
      previousRent: unit.currentRent || unit.baseRent,
      newRent: newRent,
      reason: reason,
      updatedBy: user.uid,
    };

    const unitRef = doc(getFirestore(), table, unitId);
    await updateDoc(unitRef, {
      currentRent: newRent,
      rentHistory: [...(unit.rentHistory || []), rentHistoryEntry],
      updatedAt: new Date(),
    });

    return await queryOne({ table, id: unitId });
  },

  getRentHistory: async (unitId) => {
    const unit = await queryOne({ table, id: unitId });
    return unit?.rentHistory || [];
  },

  getUnitsByRentRange: async (ownerId, minRent, maxRent) => {
    const data = await queryMany({
      table,
      conditions: [
        ["ownerId", "==", ownerId],
        ["currentRent", ">=", minRent],
        ["currentRent", "<=", maxRent],
      ],
    });
    return data;
  },
};

export default unitModel;

// Legacy export for backward compatibility
export const Unit = {
  id: "",
  name: "",
  address: {
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
  },
  unitType: "", // 'single_family', 'multi_family', 'apartment', 'condo', 'townhouse'
  units: 1,
  bedrooms: 0,
  bathrooms: 0,
  squareFootage: 0,
  yearBuilt: 0,
  purchasePrice: 0,
  currentValue: 0,
  baseRent: 0, // Base rent for this unit
  currentRent: 0, // Current market rent
  rentHistory: [], // Array of rent changes over time
  propertyTax: 0,
  insurance: 0,
  mortgage: {
    principal: 0,
    monthlyPayment: 0,
    interestRate: 0,
    term: 30,
  },
  status: "active", // 'active', 'inactive', 'maintenance', 'vacant'
  ownerId: "",
  createdAt: null,
  updatedAt: null,
};
